/**
 * Palantir PMS - Software para la gestión de la industria hotelera
 * Palantir PMS - Software for hotel industry management
 * Autor / Author: Alberto Castro Ull
 * Fecha de creación / Created on: 31/07/2025
 * Todos los derechos reservados / All rights reserved.
 * Este software es propiedad de Alberto Castro Ull.
 * This software is the property of Alberto Castro Ull.
 * Su uso está restringido a clientes con licencia emitida por Markulia Group, S.L.
 * Use is restricted to clients licensed by Markulia Group, S.L.
 */

import axios from "../api/axiosInstance";
import { getGuestContext } from "../utils/guestHelpers";
import { useState, useEffect, useRef } from "react";



export const useCustomerForm = ({
  showNewCustomerForm,
  editingChildIndex,
  editingAccompanyingIndex,
  bookerData,
  setBookerData,
  customerData,
  setCustomerData,
  formData,
  setGroupReservations,
  requiredCustomerFieldsReservation,
  settings,
  navigate,
  reservationIdRef,
  pendingMainGuestId,
  businessUnitId,
  setEditingChildIndex,
  setShowNewCustomerForm,
  guestCustomers,
  setGuestCustomers,
  guestChildren,
  setGuestChildren,
  setEditingAccompanyingIndex
}) => {

   const [customerFormData, setCustomerFormData, ] = useState({
      first_name: "",
      last_name: "",
      middle_name: "",
      second_last_name: "",
      email: "",
      phone: "",
      address: "",
      postal_code: "",
      country: "",
      city: "",
      gender: "",
      birth_date: "",
      document_type: "",
      document_number: "",
      is_vip: false,
      vip_description: "",
      photo_url: "",
      level: "",
    });

    const handleCustomerChange = (e) => {
      const { name, value } = e.target;
      setCustomerFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    };

      const handleCustomerCountryChange = (selectedOption) => {
        setCustomerFormData((prev) => ({
          ...prev,
          country: selectedOption ? selectedOption.value : "",
        }));
      };

    const getCurrentGuestType = () =>
    getGuestContext({
        showNewCustomerForm,
        editingChildIndex,
        editingAccompanyingIndex,
    });
    
  const handleSubmitCustomer = async (requiredFields) => {
    console.log("⚡️ [CUSTOMER] Entrando en handleSubmitCustomer. customerFormData:", customerFormData);
    const guestType = getCurrentGuestType({
      showNewCustomerForm,
      editingChildIndex,
      editingAccompanyingIndex,
    });

    
        console.log("🧠 guestType detectado:", guestType);

        // Calcula guestIndex de forma robusta
        // ---- guestIndex robusto y orden correcto ----
        let guestIndex = null;

        if (guestType === "accompanying") {
          if (
            typeof editingAccompanyingIndex === "number" &&
            editingAccompanyingIndex >= 0
          ) {
            guestIndex = editingAccompanyingIndex;
          } else if (
            typeof showNewCustomerForm === "string" &&
            showNewCustomerForm.startsWith("accompanying-")
          ) {
            guestIndex = parseInt(showNewCustomerForm.split("-")[1], 10);
          }
        }

        if (guestType === "child") {
          if (
            typeof editingChildIndex === "number" &&
            editingChildIndex >= 0
          ) {
            guestIndex = editingChildIndex;
          } else if (
            typeof showNewCustomerForm === "string" &&
            showNewCustomerForm.startsWith("child-")
          ) {
            guestIndex = parseInt(showNewCustomerForm.split("-")[1], 10);
          }
        }

        console.log(
          "[DEBUG][handleSubmitCustomer] guestType:", guestType,
          "| guestIndex:", guestIndex,
          "| editingAccompanyingIndex:", editingAccompanyingIndex,
          "| editingChildIndex:", editingChildIndex,
          "| showNewCustomerForm:", showNewCustomerForm
        );

    
    
        if (guestType === "booker") {
          const birthDate = customerFormData.birth_date ? new Date(customerFormData.birth_date) : null;
          const minAgeBooker = settings?.min_age_booker;
    
          if (birthDate && minAgeBooker !== null && minAgeBooker !== undefined) {
            const today = new Date();
            let age = today.getFullYear() - birthDate.getFullYear();
            const m = today.getMonth() - birthDate.getMonth();
            if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
              age--;
            }
    
            if (age < minAgeBooker) {
              alert(`❌ The booker must be at least ${minAgeBooker} years old. Current age: ${age}`);
              return;
            }
          }
        }
    
        console.log("🎯 guestType al guardar:", guestType);
        if (guestType === "main_guest") {
          const birthDate = customerFormData.birth_date ? new Date(customerFormData.birth_date) : null;
          const minAgeMainGuest = settings?.min_age_main_guest;
    
          if (birthDate && minAgeMainGuest !== null && minAgeMainGuest !== undefined) {
            const today = new Date();
            let age = today.getFullYear() - birthDate.getFullYear();
            const m = today.getMonth() - birthDate.getMonth();
            if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
              age--;
            }
    
            if (age < minAgeMainGuest) {
              alert(`❌ The main guest must be at least ${minAgeMainGuest} years old. Current age: ${age}`);
              return;
            }
          }
        }
    
        const missingFields = requiredFields.filter(
          (field) => !customerFormData[field] || customerFormData[field].trim() === ""
        );
    
        if (missingFields.length > 0) {
          alert(`Missing required fields:\n${missingFields.join(", ")}`);
          return;
        }
    
      
        try {
          const cleanCustomerData = {
            ...customerFormData,
            document_type: customerFormData.document_type === "" ? null : customerFormData.document_type,
            document_number: customerFormData.document_number === "" ? null : customerFormData.document_number,
            salutation: customerFormData.salutation === "" ? null : customerFormData.salutation,
            gender: customerFormData.gender === "" ? null : customerFormData.gender,
            vip_level: customerFormData.vip_level === "" ? null : customerFormData.vip_level,
            birth_date: isNaN(new Date(customerFormData.birth_date).getTime())
              ? null
              : customerFormData.birth_date,
          };
    
          // 🧠 Validación clara por tipo de cliente (Main Guest)
          try {
            const settingsRes = await axios.get(`${process.env.REACT_APP_API_URL}/api/hotel-settings`, {
              params: { business_unit_id: businessUnitId }
            });
    
            const maxAgeChild = settingsRes.data.max_age_child;
            const maxAgeBaby = settingsRes.data.max_age_baby;
            const birthDate = cleanCustomerData.birth_date ? new Date(cleanCustomerData.birth_date) : null;
    
           
            
    
            // 🧠 Validación clara para Accompanying Guest
            if (birthDate && guestType === "accompanying") {
              
              const today = new Date();
              let age = today.getFullYear() - birthDate.getFullYear();
              const m = today.getMonth() - birthDate.getMonth();
              if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
                age--;
              }
    
              if (age <= maxAgeBaby) {
                alert(`❌ Accompanying guest must be older than maximum age for baby (${maxAgeBaby}).\nCurrent age: ${age}`);
                return;
              }
            }
    
            // 🧠 Validación clara para Child
            if (birthDate && guestType === "child") {
              const today = new Date();
              let age = today.getFullYear() - birthDate.getFullYear();
              const m = today.getMonth() - birthDate.getMonth();
              if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
                age--;
              }
    
              if (age <= maxAgeBaby || age > maxAgeChild) {
                alert(`❌ Child must be older than maximum age for baby (${maxAgeBaby}) and equal or younger than maximum age for child (${maxAgeChild}).\nCurrent age: ${age}`);
                return;
              }
            }
    
    
          } catch (error) {
            console.error("❌ Error checking guest age:", error);
            alert("Error validating guest age.");
            return;
          }
    
    
      
          let newCustomerId = null;
    
          let finalCustomerData = null;
    
          console.log("📦 Payload enviado al backend (PUT o POST):", cleanCustomerData);
    
          if (customerFormData.id) {
            console.log("✏️ Editando cliente existente con ID:", customerFormData.id, "Payload:", cleanCustomerData);
            const putResponse = await axios.put(`${process.env.REACT_APP_API_URL}/api/customers/${customerFormData.id}`, cleanCustomerData);
            newCustomerId = putResponse.data.id;
            finalCustomerData = putResponse.data;
          } else {
            console.log("🆕 Creando cliente nuevo. Payload:", cleanCustomerData);
            const postResponse = await axios.post(`${process.env.REACT_APP_API_URL}/api/customers`, cleanCustomerData);
            newCustomerId = postResponse.data.id;
            finalCustomerData = postResponse.data;
          }
    
    
      
          const reservationIdMatch = window.location.pathname.match(/\/new-reservation\/(\d+)/);
          const reservationId = reservationIdMatch ? reservationIdMatch[1] : null;
    
            
          const currentURL = new URL(window.location.href);
          
          const mainGuestId = currentURL.searchParams.get("customer_id");
    
      
          if (!reservationId) {
            alert("Missing reservation ID");
            return;
          }
        
          console.log("🌐 Parámetros leídos justo antes del IF:");
          console.log("➡ guestType =", guestType);
          console.log("➡ guestIndex =", guestIndex);
          console.log("➡ mainGuestId =", mainGuestId);
    
    
          if (guestType === "accompanying" && guestIndex !== null && !isNaN(guestIndex)) {
          console.log("👥 Guest creado como acompañante");

         try {
        // 1) ¿Ya existe en DRAFT guests?
        const guestsRes = await axios.get(
          `/api/reservation-drafts/draft-guests/by-draft/${reservationIdRef.current}`
        );

        const exists = (guestsRes.data || []).find(g =>
          g.customer_id === newCustomerId &&
          Number(g.guest_index) === Number(guestIndex) &&
          g.is_child === false &&
          g.is_main_guest === false
        );

        if (!exists) {
          await axios.post("/api/reservation-drafts/draft-guests", {
            reservation_draft_id: reservationIdRef.current,
            customer_id: newCustomerId,
            is_main_guest: false,
            is_child: false,
            guest_index: Number(guestIndex),
          });

          console.log("✅ Acompañante guardado correctamente en reservation_draft_guests");
        } else {
          console.log("⚠️ El draft_guest accompanying ya existe. Solo actualizado el customer.");
        }

        // 2) Recarga DRAFT guests para reconstruir slots
        const guestsReload = await axios.get(
          `/api/reservation-drafts/draft-guests/by-draft/${reservationIdRef.current}`
        );

        const rebuiltAccompanying = [];
        (guestsReload.data || [])
          .filter(g => !g.is_main_guest && !g.is_child)
          .forEach(g => {
            const idx = Number(g.guest_index);
            if (!isNaN(idx)) rebuiltAccompanying[idx] = g.customer || g;
          });

        setGuestCustomers(rebuiltAccompanying);

      } catch (error) {
        console.error("❌ Error al guardar acompañante en reservation_draft_guests:", error);
        alert("Error saving accompanying guest to draft.");
        return;
      }


          setShowNewCustomerForm(false);
          setEditingAccompanyingIndex(null);
          setCustomerFormData({});
          window.__internalNavigation = true;
          setTimeout(() => {
            window.__internalNavigation = false;
          }, 1000);

          return;
        }

    
          
          
        else if (guestType === "child" && guestIndex !== null && !isNaN(guestIndex)) {
        console.log("👶 Guest creado como niño");
        try {
        // 1. ¿Ya existe en draft_guests?
        const guestsRes = await axios.get(
          `/api/reservation-drafts/draft-guests/by-draft/${reservationIdRef.current}`
        );

        const exists = (guestsRes.data || []).find(g =>
          g.customer_id === newCustomerId &&
          Number(g.guest_index) === Number(guestIndex) &&
          g.is_child === true
        );

        if (!exists) {
          await axios.post("/api/reservation-drafts/draft-guests", {
            reservation_draft_id: reservationIdRef.current,
            customer_id: newCustomerId,
            is_main_guest: false,
            is_child: true,
            guest_index: Number(guestIndex),
          });

          console.log("✅ Niño guardado correctamente en draft_guests");
        } else {
          console.log("⚠️ El draft_guest child ya existe. Solo actualizado el customer.");
        }

        // 2. Recarga todos los niños del draft desde la base de datos
        const guestsReload = await axios.get(
          `/api/reservation-drafts/draft-guests/by-draft/${reservationIdRef.current}`
        );

        const rebuiltChildren = [];
        (guestsReload.data || [])
          .filter(g => g.is_child)
          .forEach(g => {
            const idx = Number(g.guest_index);
            if (!isNaN(idx)) rebuiltChildren[idx] = g.customer || g;
          });

        setGuestChildren(rebuiltChildren);
        setShowNewCustomerForm(false);
        setEditingChildIndex(null);
        setCustomerFormData({});

      } catch (error) {
        console.error("❌ Error al guardar niño en draft_guests:", error);
        alert("Error saving child to reservation.");
        return;
      }

      window.__internalNavigation = true;
      setTimeout(() => {
        window.__internalNavigation = false;
      }, 1000);


        return;
      }

          
          else if (
            guestType === "booker" &&
            (customerFormData.id === bookerData?.id || showNewCustomerForm === "booker")
          ) {
            console.log("📘 Cliente creado como booker");
    
            setBookerData(finalCustomerData);
            setShowNewCustomerForm(false);

            if (
              customerData?.id &&
              finalCustomerData?.id &&
              customerData.id === finalCustomerData.id
            ) {
              setCustomerData(finalCustomerData);
            }
    
            if (formData.reservation_group_id && finalCustomerData?.id) {
              try {
                await axios.patch(
                  `${process.env.REACT_APP_API_URL}/api/reservation-drafts/draft-groups/${formData.reservation_group_id}`,
                  { booker_id: finalCustomerData.id, updated_by: "UserSelect" }
                );

                console.log("✅ booker_id guardado correctamente en reservation_groups");
    
                // 🔁 Recargar reservas del grupo
                const updatedGroup = await axios.get(
                  `${process.env.REACT_APP_API_URL}/api/reservation-drafts/draft-groups/${formData.reservation_group_id}/reservations`
                );

                setGroupReservations(updatedGroup.data);
                console.log("📦 [setGroupReservations] (booker assignment) Group reloaded:", updatedGroup.data);
    
    
                // ✅ Recargar datos del booker explícitamente
                try {
                  const bookerRes = await axios.get(`${process.env.REACT_APP_API_URL}/api/customers/${finalCustomerData.id}`);
                  setBookerData(bookerRes.data);
                  console.log("✅ Booker recargado después de asignación:", bookerRes.data);
                } catch (err) {
                  console.warn("⚠️ No se pudo recargar booker después de asignación:", err);
                }
    
                // 🧭 Forzar recarga de la pestaña actual
                // 🧭 Forzar recarga de la pestaña actual tras asignar booker
                const currentId = reservationIdRef.current;
                if (currentId) {
                  const queryParams = new URLSearchParams(window.location.search);
    
                  // ✅ AÑADIR ESTAS DOS LÍNEAS para marcar la navegación como interna
                  window.__internalNavigation = true;
                  setTimeout(() => {
                    window.__internalNavigation = false;
                  }, 1000);
    
                  navigate(`/new-reservation/${currentId}?${queryParams.toString()}`, { replace: true });
                }
    
    
              } catch (error) {
                console.error("❌ Error al guardar booker_id en reservation_groups:", error);
                alert("Error saving booker to reservation group.");
              }
            }
    
    
            return; // 🛑 Salimos para no ejecutar el bloque de main guest
          }
    
    
          
    
          else {
           console.log("🧍 Guest creado como main guest");
              
            pendingMainGuestId.current = newCustomerId;
            console.log("✅ mainGuestId ref guardado:", pendingMainGuestId.current);
            
            navigate(`/new-reservation/${reservationIdRef.current}?business_unit_id=${businessUnitId}`, { replace: true });
            // Recargar el customer real desde la DB para evitar datos cacheados incompletos
            try {
              const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/customers/${newCustomerId}`);
              setCustomerData((prev) => {
                return prev?.__manual_insert ? { ...res.data, __manual_insert: true } : res.data;
              });

              if (
                bookerData?.id &&
                res.data?.id &&
                bookerData.id === res.data.id
              ) {
                setBookerData(res.data);
              }
    
              console.log("🔁 Main guest cargado desde DB tras submit:", res.data);
            } catch (err) {
              console.warn("⚠️ No se pudo recargar el main guest tras submit:", err);
            }
    
            setShowNewCustomerForm(false);
    
            try {
              
              const existingGuestsRes = await axios.get(
                `${process.env.REACT_APP_API_URL}/api/reservation-drafts/draft-guests/by-draft/${reservationIdRef.current}`
              );

              const alreadyMainGuest = existingGuestsRes.data.some(
                g => g.customer_id === newCustomerId && g.is_main_guest
              );

              if (alreadyMainGuest) {
                console.log("⚠️ El main guest ya está en reservation_draft_guests. No se reinserta.");
              } else {
                await axios.post(`${process.env.REACT_APP_API_URL}/api/reservation-drafts/draft-guests`, {
                  reservation_draft_id: reservationIdRef.current,
                  customer_id: newCustomerId,
                  is_main_guest: true,
                  is_child: false,
                  guest_index: null,
                  relationship: null,
                });

                console.log("✅ Main guest insertado correctamente en reservation_draft_guests");
                pendingMainGuestId.current = newCustomerId;
              }

    
              try {
                const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/customers/${newCustomerId}`);
                setCustomerData((prev) => {
                  return prev?.__manual_insert ? { ...res.data, __manual_insert: true } : res.data;
                });
    
                console.log("🔁 Main guest recargado desde DB:", res.data);
              } catch (err) {
                console.warn("⚠️ No se pudo recargar el main guest después del insert:", err);
              }
              
            } catch (error) {
              console.error("❌ Error insertando main guest en reservation_guests:", error);
            }
            
    
            // Solo limpiamos editingChildIndex si NO estamos creando/guardando un niño
            if (guestType !== "child") {
              setEditingChildIndex(null);
            }
    
          }
          
    
    
        } catch (error) {
          console.error("❌ Error al guardar el cliente desde NewReservation:", error);
          alert("Error saving customer. Please try again.");
        }      
  };

  // Función de cancelación
    const handleCancel = () => {
    setShowNewCustomerForm(false);
    setEditingAccompanyingIndex(null);
    setEditingChildIndex(null);
    setCustomerFormData({});
  };


  return { customerFormData, setCustomerFormData, handleCustomerChange, handleSubmitCustomer, handleCustomerCountryChange, handleCancel };
};
